/**
 * Meeting recordings API service (AI note taker)
 */

import type {
  CreateMeetingRecordingResponseType,
  CreateMeetingRecordingType,
  MeetingRecordingAudioUrlType,
  MeetingRecordingStatusEnum,
  MeetingTranscriptType,
  UpdateMeetingRecordingType,
} from '@scrambl/shared'

import { apiClient } from './api-client'
import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'

export interface MeetingRecording {
  id: string
  organizationId: string
  createdBy: string
  source: 'DESKTOP_CAPTURE' | 'BOT'
  status: MeetingRecordingStatusEnum | string
  title: string | null
  mimeType: string | null
  fileSize: string | number | null
  durationSeconds: number | null
  recordedAt: string | null
  eventId: string | null
  noteId: string | null
  moduleType: string | null
  moduleId: string | null
  errorMessage: string | null
  errorStage: string | null
  /** Transcript excerpt around the first match (present in search results) */
  snippet?: string | null
  createdAt: string
  updatedAt: string
  creator?: {
    id: string
    firstName: string | null
    lastName: string | null
    displayName: string | null
    email: string | null
    image: string | null
  }
}

class MeetingRecordingsApiService extends BaseApi<
  MeetingRecording,
  CreateMeetingRecordingType,
  UpdateMeetingRecordingType
> {
  constructor() {
    super({
      baseEndpoint: '/meeting-recordings',
      responseNormalization: true,
    })
  }

  async listRecordings(params?: {
    page?: number
    limit?: number
    search?: string
  }): Promise<{ recordings: MeetingRecording[]; total: number }> {
    const query = this.buildQueryParams({
      page: params?.page,
      limit: params?.limit,
      search: params?.search || undefined,
    })
    return apiClient<{ recordings: MeetingRecording[]; total: number }>(
      `${this.config.baseEndpoint}${query}`,
    )
  }

  async get(id: string): Promise<MeetingRecording> {
    return apiClient<MeetingRecording>(`${this.config.baseEndpoint}/${id}`)
  }

  async getTranscript(id: string): Promise<MeetingTranscriptType> {
    return apiClient<MeetingTranscriptType>(
      `${this.config.baseEndpoint}/${id}/transcript`,
    )
  }

  /** Short-lived presigned URL for in-app audio playback. */
  async getAudioUrl(id: string): Promise<MeetingRecordingAudioUrlType> {
    return apiClient<MeetingRecordingAudioUrlType>(
      `${this.config.baseEndpoint}/${id}/audio-url`,
    )
  }

  /** Regenerate the AI summary with a different template — appends a NEW
   *  Summary page to the note (existing pages are kept). */
  async resummarize(
    id: string,
    templateRef: string | null,
  ): Promise<{ noteId: string; pageId: string; pageTitle: string }> {
    return apiClient(`${this.config.baseEndpoint}/${id}/resummarize`, {
      method: 'POST',
      body: { templateRef },
    })
  }

  /** Short AI summary of the live caption transcript ("catch me up"). */
  async catchUp(transcript: string): Promise<{ summary: string }> {
    return apiClient<{ summary: string }>(
      `${this.config.baseEndpoint}/catch-up`,
      { method: 'POST', body: { transcript } },
    )
  }

  /** Suggest a record to link a meeting to, from attendee email domains. */
  async suggestLink(attendeeEmails: string[]): Promise<{
    suggestion: { moduleType: string; moduleId: string; name: string } | null
  }> {
    return apiClient(`${this.config.baseEndpoint}/suggest-link`, {
      method: 'POST',
      body: { attendeeEmails },
    })
  }

  /** Attach in-meeting scratchpad notes (merged into the AI summary). */
  async updateUserNotes(id: string, notes: string): Promise<MeetingRecording> {
    return apiClient<MeetingRecording>(
      `${this.config.baseEndpoint}/${id}/notes`,
      { method: 'PATCH', body: { notes } },
    )
  }

  /** Names to offer when renaming a transcript speaker (attendees + members). */
  async getSpeakerCandidates(id: string): Promise<{
    recorderName: string | null
    candidates: { name: string; email: string | null; source: string }[]
  }> {
    return apiClient(
      `${this.config.baseEndpoint}/${id}/speaker-candidates`,
    )
  }

  /** Replace the transcript's speaker display-name map. */
  async updateSpeakers(
    id: string,
    speakers: Record<string, string>,
  ): Promise<MeetingTranscriptType> {
    return apiClient<MeetingTranscriptType>(
      `${this.config.baseEndpoint}/${id}/speakers`,
      { method: 'PATCH', body: { speakers } },
    )
  }

  async createUploadSession(
    body: CreateMeetingRecordingType,
  ): Promise<CreateMeetingRecordingResponseType> {
    return apiClient<CreateMeetingRecordingResponseType>(
      this.config.baseEndpoint,
      { method: 'POST', body },
    )
  }

  async getPartUrls(
    id: string,
    partNumbers: number[],
  ): Promise<{ partNumber: number; url: string }[]> {
    return apiClient<{ partNumber: number; url: string }[]>(
      `${this.config.baseEndpoint}/${id}/part-urls`,
      { method: 'POST', body: { partNumbers } },
    )
  }

  async completeUpload(
    id: string,
    parts: { partNumber: number; etag: string }[],
    durationSeconds?: number,
  ): Promise<MeetingRecording> {
    return apiClient<MeetingRecording>(
      `${this.config.baseEndpoint}/${id}/complete`,
      {
        method: 'POST',
        body: { parts, ...(durationSeconds ? { durationSeconds } : {}) },
      },
    )
  }

  /**
   * Upload a browser-recorded audio blob through the presigned multipart
   * flow (used by the web mic recorder; the desktop app uploads from Rust).
   */
  async uploadAudioBlob(
    blob: Blob,
    options: {
      fileName: string
      mimeType: string
      durationSeconds?: number
      moduleType?: string
      moduleId?: string
      title?: string
      summaryTemplate?: string
      userNotes?: string
      onProgress?: (uploadedBytes: number, totalBytes: number) => void
    },
  ): Promise<{ recordingId: string }> {
    const session = await this.createUploadSession({
      fileName: options.fileName,
      mimeType: options.mimeType,
      fileSize: blob.size,
      durationSeconds: options.durationSeconds,
      recordedAt: new Date(),
      title: options.title,
      moduleType: options.moduleType,
      moduleId: options.moduleId,
      metadata: {
        source: 'web',
        ...(options.summaryTemplate
          ? { summaryTemplate: options.summaryTemplate }
          : {}),
        ...(options.userNotes ? { userNotes: options.userNotes } : {}),
      },
    })

    const partSize = session.partSize
    const totalParts = Math.max(1, Math.ceil(blob.size / partSize))
    const parts: { partNumber: number; etag: string }[] = []
    let uploadedBytes = 0

    const BATCH = 10
    for (let start = 1; start <= totalParts; start += BATCH) {
      const numbers = Array.from(
        { length: Math.min(BATCH, totalParts - start + 1) },
        (_, index) => start + index,
      )
      const urls = await this.getPartUrls(session.recordingId, numbers)
      for (const part of urls) {
        const offset = (part.partNumber - 1) * partSize
        const body = blob.slice(offset, Math.min(offset + partSize, blob.size))
        const response = await fetch(part.url, { method: 'PUT', body })
        if (!response.ok) {
          throw new Error(`Part ${part.partNumber} upload failed (${response.status})`)
        }
        const etag = response.headers.get('ETag')?.replace(/"/g, '')
        if (!etag) {
          throw new Error('Upload response missing ETag (check bucket CORS)')
        }
        parts.push({ partNumber: part.partNumber, etag })
        uploadedBytes += body.size
        options.onProgress?.(uploadedBytes, blob.size)
      }
    }

    await this.completeUpload(
      session.recordingId,
      parts.sort((a, b) => a.partNumber - b.partNumber),
      options.durationSeconds,
    )
    return { recordingId: session.recordingId }
  }

  async retry(id: string): Promise<MeetingRecording> {
    return apiClient<MeetingRecording>(
      `${this.config.baseEndpoint}/${id}/retry`,
      { method: 'POST' },
    )
  }

  async updateRecording(
    id: string,
    body: UpdateMeetingRecordingType,
  ): Promise<MeetingRecording> {
    return apiClient<MeetingRecording>(`${this.config.baseEndpoint}/${id}`, {
      method: 'PATCH',
      body,
    })
  }

  async remove(id: string): Promise<void> {
    await apiClient(`${this.config.baseEndpoint}/${id}`, { method: 'DELETE' })
  }
}

export const meetingRecordingsApi = createSingletonApi(
  MeetingRecordingsApiService,
)
